/* eslint-disable @typescript-eslint/no-explicit-any */
import { JSX } from "react";
import { ComponentTypes } from "../componentTypes";
import type { RendererConfig } from "./rendererConfig";
import { PageHeaderRenderer } from "./blocks/PageHeaderRenderer";
import { ActivityGridRenderer } from "./blocks/ActivityGridRenderer";
import { PaginatedActivityGridRenderer } from "./blocks/PaginatedActivityGridRenderer";
import { PersonCardGridRenderer } from "./blocks/PersonCardGridRenderer";
import { SectionHeadingRenderer } from "./blocks/SectionHeadingRenderer";
import { LinkTileGridRenderer } from "./blocks/LinkTileGridRenderer";
import { EventGridRenderer } from "./blocks/EventGridRenderer";
import { ArticleGridRenderer } from "./blocks/ArticleGridRenderer";
import { LatestArticleGridRenderer } from "./blocks/LatestArticleGridRenderer";
import { CalloutRenderer } from "./blocks/CalloutRenderer";
import { CtaBannerRenderer } from "./blocks/CtaBannerRenderer";
import { StatsGridRenderer } from "./blocks/StatsGridRenderer";
import { SplitImageCalloutRenderer } from "./blocks/SplitImageCalloutRenderer";
import { BlogArticleGridRenderer } from "./blocks/BlogArticleGridRenderer";
import { TextBlockRenderer } from "./blocks/TextBlockRenderer";
import { RichTextRenderer } from "./blocks/RichTextRenderer";
import { GalleryRenderer } from "./blocks/GalleryRenderer";
import { BannerImageRenderer } from "./blocks/BannerImageRenderer";
import { FullWidthImageRenderer } from "./blocks/FullWidthImageRenderer";
import { IconCardGridRenderer } from "./blocks/IconCardGridRenderer";
import { TextCardGridRenderer } from "./blocks/TextCardGridRenderer";
import { LabeledTextGridRenderer } from "./blocks/LabeledTextGridRenderer";
import { CompanyReportGridRenderer } from "./blocks/CompanyReportGridRenderer";
import { ContactFormRenderer } from "./blocks/ContactFormRenderer";
import { SeriesExplorerRenderer } from "./blocks/SeriesExplorerRenderer";
import { KeyMetricsExplorerRenderer } from "./blocks/KeyMetricsExplorerRenderer";
import { FxRatesExplorerRenderer } from "./blocks/FxRatesExplorerRenderer";
import { HeroRenderer } from "./blocks/HeroRenderer";
import { NewsSectionRenderer } from "./blocks/NewsSectionRenderer";
import { NewsListRenderer } from "./blocks/NewsListRenderer";
import { StatsSummaryRenderer } from "./blocks/StatsSummaryRenderer";
import { AudienceToolsRenderer } from "./blocks/AudienceToolsRenderer";
import { SearchBarRenderer } from "./blocks/SearchBarRenderer";
import { PublicationsUpdatesRenderer } from "./blocks/PublicationsUpdatesRenderer";
import { TextWithSideImageRenderer } from "./blocks/TextWithSideImageRenderer";
import { TextWithSidePanelRenderer } from "./blocks/TextWithSidePanelRenderer";
import { SingleLinkRenderer } from "./blocks/SingleLinkRenderer";
import { LegislationListRenderer } from "./blocks/LegislationListRenderer";
import { NumberedListRenderer } from "./blocks/NumberedListRenderer";
import { TwoColumnListRenderer } from "./blocks/TwoColumnListRenderer";
import { NewsletterSignupRenderer } from "./blocks/NewsletterSignupRenderer";
import { ArticleHeroRenderer } from "./blocks/ArticleHeroRenderer";
import { AccordionSectionRenderer } from "./blocks/AccordionSectionRenderer";
import { BlueCalloutRenderer } from "./blocks/BlueCalloutRenderer";
import { CoordonneesRenderer } from "./blocks/CoordonneesRenderer";
import { DynamicComponentsRenderer } from "./blocks/DynamicComponentsRenderer";
import { KeyIndicatorsBarRenderer } from "./blocks/KeyIndicatorsBarRenderer";
import { InformationHubRenderer } from "./blocks/InformationHubRenderer";
import { MarketsBoardRenderer } from "./blocks/MarketsBoardRenderer";
import { DataRecordDetailRenderer } from "./blocks/DataRecordDetailRenderer";
import { ProductBuyBoxRenderer } from "./blocks/ProductBuyBoxRenderer";
import { PartsFinderRenderer } from "./blocks/PartsFinderRenderer";
import { SpecialtyCardsRenderer } from "./blocks/SpecialtyCardsRenderer";
import { StepsSectionRenderer } from "./blocks/StepsSectionRenderer";
import { TrustBarRenderer } from "./blocks/TrustBarRenderer";
import { ComparisonTableRenderer } from "./blocks/ComparisonTableRenderer";
import { WarrantyMatrixRenderer } from "./blocks/WarrantyMatrixRenderer";
import { BrandStripRenderer } from "./blocks/BrandStripRenderer";
import { ProQuoteFormRenderer } from "./blocks/ProQuoteFormRenderer";
import { VideoEmbedRenderer } from "./blocks/VideoEmbedRenderer";
import { GovernorListRenderer } from "./blocks/GovernorListRenderer";
import { ViceGovernorListRenderer } from "./blocks/ViceGovernorListRenderer";
import { GovernorHeroRenderer } from "./blocks/GovernorHeroRenderer";
import { BilletTabsRenderer } from "./blocks/BilletTabsRenderer";
import { TextWithFigureRenderer } from "./blocks/TextWithFigureRenderer";

/**
 * Renders a single page component. The `component` is intentionally untyped here (each renderer
 * validates its own content shape); `config` carries cross-cutting render context (e.g. contactInfo).
 */
export type ComponentRenderer = (component: any, config?: RendererConfig) => JSX.Element | null;

/**
 * componentType -> renderer. This replaces PageRenderer's former 60-case `switch`: adding a
 * component type is one entry here (plus its import), and the registry's exhaustiveness is
 * unit-tested, so a missing entry can never silently render nothing. The mapping is a 1:1 mirror of
 * the original switch — including the multi-name aliases (Hero/HomeHero, NewsSection/HomeNews, …),
 * the Margin spacer, and ContactForm receiving `config`.
 */
export const COMPONENT_REGISTRY: Record<string, ComponentRenderer> = {
  [ComponentTypes.PageHeader]: (c) => <PageHeaderRenderer component={c} />,
  [ComponentTypes.ArticleHero]: (c) => <ArticleHeroRenderer component={c} />,
  [ComponentTypes.ActivityGrid]: (c) => <ActivityGridRenderer component={c} />,
  [ComponentTypes.PaginatedActivityGrid]: (c) => <PaginatedActivityGridRenderer component={c} />,
  [ComponentTypes.PersonCardGrid]: (c) => <PersonCardGridRenderer component={c} />,
  [ComponentTypes.SectionHeading]: (c) => <SectionHeadingRenderer component={c} />,
  [ComponentTypes.LinkTileGrid]: (c) => <LinkTileGridRenderer component={c} />,
  [ComponentTypes.EventGrid]: (c) => <EventGridRenderer component={c} />,
  [ComponentTypes.ArticleGrid]: (c) => <ArticleGridRenderer component={c} />,
  [ComponentTypes.LatestArticleGrid]: (c) => <LatestArticleGridRenderer component={c} />,
  [ComponentTypes.InformationHub]: (c) => <InformationHubRenderer component={c} />,
  [ComponentTypes.MarketsBoard]: (c) => <MarketsBoardRenderer component={c} />,
  [ComponentTypes.DataRecordDetail]: (c) => <DataRecordDetailRenderer component={c} />,
  [ComponentTypes.ProductBuyBox]: (c) => <ProductBuyBoxRenderer component={c} />,
  [ComponentTypes.PartsFinder]: (c) => <PartsFinderRenderer component={c} />,
  [ComponentTypes.SpecialtyCards]: (c) => <SpecialtyCardsRenderer component={c} />,
  [ComponentTypes.StepsSection]: (c) => <StepsSectionRenderer component={c} />,
  [ComponentTypes.TrustBar]: (c) => <TrustBarRenderer component={c} />,
  [ComponentTypes.ComparisonTable]: (c) => <ComparisonTableRenderer component={c} />,
  [ComponentTypes.WarrantyMatrix]: (c) => <WarrantyMatrixRenderer component={c} />,
  [ComponentTypes.BrandStrip]: (c) => <BrandStripRenderer component={c} />,
  [ComponentTypes.ProQuoteForm]: (c) => <ProQuoteFormRenderer component={c} />,
  [ComponentTypes.Callout]: (c) => <CalloutRenderer component={c} />,
  [ComponentTypes.StatsGrid]: (c) => <StatsGridRenderer component={c} />,
  [ComponentTypes.SplitImageCallout]: (c) => <SplitImageCalloutRenderer component={c} />,
  [ComponentTypes.TextCardGrid]: (c) => <TextCardGridRenderer component={c} />,
  [ComponentTypes.LabeledTextGrid]: (c) => <LabeledTextGridRenderer component={c} />,
  [ComponentTypes.CompanyReportGrid]: (c) => <CompanyReportGridRenderer component={c} />,
  [ComponentTypes.BlogArticleGrid]: (c) => <BlogArticleGridRenderer component={c} />,
  [ComponentTypes.TextBlock]: (c) => <TextBlockRenderer component={c} />,
  [ComponentTypes.RichText]: (c) => <RichTextRenderer component={c} />,
  [ComponentTypes.Gallery]: (c) => <GalleryRenderer component={c} />,
  [ComponentTypes.VideoEmbed]: (c) => <VideoEmbedRenderer component={c} />,
  [ComponentTypes.GovernorList]: (c) => <GovernorListRenderer component={c} />,
  [ComponentTypes.GovernorHero]: (c) => <GovernorHeroRenderer component={c} />,
  [ComponentTypes.ViceGovernorList]: (c) => <ViceGovernorListRenderer component={c} />,
  [ComponentTypes.BilletTabs]: (c) => <BilletTabsRenderer component={c} />,
  [ComponentTypes.TextWithFigure]: (c) => <TextWithFigureRenderer component={c} />,
  [ComponentTypes.Margin]: () => <div aria-hidden="true" />,
  [ComponentTypes.BannerImage]: (c) => <BannerImageRenderer component={c} />,
  [ComponentTypes.FullWidthImage]: (c) => <FullWidthImageRenderer component={c} />,
  [ComponentTypes.IconCardGrid]: (c) => <IconCardGridRenderer component={c} />,
  [ComponentTypes.TextWithSideImage]: (c) => <TextWithSideImageRenderer component={c} />,
  [ComponentTypes.TextWithSidePanel]: (c) => <TextWithSidePanelRenderer component={c} />,
  [ComponentTypes.SingleLink]: (c) => <SingleLinkRenderer component={c} />,
  [ComponentTypes.LegislationList]: (c) => <LegislationListRenderer component={c} />,
  [ComponentTypes.NumberedList]: (c) => <NumberedListRenderer component={c} />,
  [ComponentTypes.TwoColumnList]: (c) => <TwoColumnListRenderer component={c} />,
  [ComponentTypes.NewsletterSignup]: (c) => <NewsletterSignupRenderer component={c} />,
  [ComponentTypes.ContactForm]: (c, config) => <ContactFormRenderer component={c} config={config} />,
  [ComponentTypes.SeriesExplorer]: (c) => <SeriesExplorerRenderer component={c} />,
  [ComponentTypes.KeyMetricsExplorer]: (c) => <KeyMetricsExplorerRenderer component={c} />,
  [ComponentTypes.FxRatesExplorer]: (c) => <FxRatesExplorerRenderer component={c} />,
  [ComponentTypes.Hero]: (c) => <HeroRenderer component={c} />,
  [ComponentTypes.HomeHero]: (c) => <HeroRenderer component={c} />,
  [ComponentTypes.NewsSection]: (c) => <NewsSectionRenderer component={c} />,
  [ComponentTypes.HomeNews]: (c) => <NewsSectionRenderer component={c} />,
  [ComponentTypes.NewsList]: (c) => <NewsListRenderer component={c} />,
  [ComponentTypes.CtaBanner]: (c) => <CtaBannerRenderer component={c} />,
  [ComponentTypes.StatsSummary]: (c) => <StatsSummaryRenderer component={c} />,
  [ComponentTypes.HomeBlueStats]: (c) => <StatsSummaryRenderer component={c} />,
  [ComponentTypes.AudienceTools]: (c) => <AudienceToolsRenderer component={c} />,
  [ComponentTypes.HomeBlueTools]: (c) => <AudienceToolsRenderer component={c} />,
  [ComponentTypes.SearchBar]: (c) => <SearchBarRenderer component={c} />,
  [ComponentTypes.PublicationsUpdates]: (c) => <PublicationsUpdatesRenderer component={c} />,
  [ComponentTypes.HomePubUpdatesSection]: (c) => <PublicationsUpdatesRenderer component={c} />,
  [ComponentTypes.AccordionSection]: (c) => <AccordionSectionRenderer component={c} />,
  [ComponentTypes.Coordonnees]: (c) => <CoordonneesRenderer component={c} />,
  [ComponentTypes.BlueCallout]: (c) => <BlueCalloutRenderer component={c} />,
  [ComponentTypes.DynamicComponents]: (c) => <DynamicComponentsRenderer component={c} />,
  [ComponentTypes.KeyIndicatorsBar]: (c) => <KeyIndicatorsBarRenderer component={c} />,
};
